/**
 * useTransform Composable
 * Handles data transformation utilities for Tax Dashboard
 */

import { computed } from 'vue';

/**
 * Transform raw data to pie chart format
 * Used for Access Channels and New vs Returning Users
 * @param {Object} rawData - Raw data with labels and series
 * @returns {Array} Transformed data with label, value, and percentage
 */
export const transformToPieChartFormat = (rawData) => {
  if (!rawData || !rawData.labels) return [];
  const total = rawData.series.reduce((sum, val) => sum + val, 0);
  return rawData.labels.map((label, idx) => ({
    label,
    value: rawData.series[idx],
    percentage: total > 0 ? Math.round((rawData.series[idx] / total) * 100) : 0,
  }));
};

/**
 * Create paginated dislike reasons computed property
 * @deprecated Use createPaginatedData instead for more flexibility
 * @param {Ref} dislikeReasons - Reactive array of dislike reasons
 * @param {Ref} page - Current page number (reactive)
 * @param {number} perPage - Items per page
 * @returns {ComputedRef} Paginated array
 */

/**
 * Create generic paginated data with optional sorting
 * @param {Ref} data - Reactive array of data
 * @param {Ref} page - Current page number (reactive)
 * @param {number} perPage - Items per page
 * @param {Function} sortFn - Optional sorting function (a, b) => number
 * @returns {Object} Object with paginatedData computed and totalPages computed
 */
export const createPaginatedData = (data, page, perPage, sortFn = null) => {
  const sortedData = computed(() => {
    if (!data.value || !Array.isArray(data.value)) return [];
    return sortFn ? [...data.value].sort(sortFn) : [...data.value];
  });

  const paginatedData = computed(() => {
    const start = (page.value - 1) * perPage;
    const end = start + perPage;
    return sortedData.value.slice(start, end);
  });

  const totalPages = computed(() => {
    return Math.ceil((sortedData.value?.length || 0) / perPage);
  });

  return {
    sortedData,
    paginatedData,
    totalPages,
  };
};

/**
 * useTransform Composable
 * @returns {Object} Transform functions
 */
export function useTransform() {
  return {
    transformToPieChartFormat,
    createPaginatedData,
  };
}
